<template>
  <div>
    <v-card>
      <v-row class="px-4 mb-2" align="center" justify="start">
        <v-col cols="12" md="4" class="text-center">
          <RowsPerPage :pagination="pagination" :disabled="isLoading" />
        </v-col>
      </v-row>
      <v-data-table
        :headers="headers"
        :items="taxJurisdictions"
        :items-per-page="pagination.per_page"
        :loading="isLoading"
        :loading-text="$t('components.noData.fetchingEntries')"
        hide-default-footer>
        <template v-slot:top>
          <v-toolbar flat>
            <v-toolbar-title>
              {{ $t('entities.taxJurisdictions.title') }}
            </v-toolbar-title>
            <v-divider inset vertical class="mx-4" />
            <small class="mr-2">{{ pagination.total }}</small>
            <RefreshButton :refresh="refresh" :loading="isLoading" />
          </v-toolbar>
        </template>
        <template v-slot:item.fiscalize="{ item }">
          <v-chip small :class="item.fiscalize ? 'green' : 'red'">
            {{ $t(`miscellaneous.${item.fiscalize ? 'yes' : 'no'}`) }}
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn
            @click="openDetailsDialog(item)"
            depressed
            x-small
            fab
            color="secondary"
            class="glow ml-2">
            <v-icon small>pageview</v-icon>
          </v-btn>
        </template>
      </v-data-table>
      <DataTableFooter :pagination="pagination" :loading="isLoading" />
    </v-card>
    <DetailsDialog
      @dialog:close="closeDetailsDialog"
      :taxJurisdiction="taxJurisdictionToView"
      :is-opened="isDetailsDialogOpened" />
  </div>
</template>

<script>
import TaxJurisdiction from '@/models/setup/TaxJurisdiction'
import DataTableFooter from '@/components/crud/DataTableFooter.vue'
import RowsPerPage from '@/components/crud/RowsPerPage.vue'
import DetailsDialog from './DetailsDialog.vue'
import { paginationWatch } from '@/mixins/crud/watch/pagination.js'
import RefreshButton from '@/components/crud/RefreshButton.vue'

const resetPagination = () => ({
  search: '',
  current_page: 1,
  from: 1,
  last_page: 0,
  path: '',
  per_page: 10,
  to: 0,
  total: 0,
})

export default {
  name: 'TaxJurisdictions',
  components: {
    DataTableFooter,
    RowsPerPage,
    DetailsDialog,
    RefreshButton,
  },
  mixins: [paginationWatch],
  data() {
    return {
      taxJurisdictions: [],
      isDetailsDialogOpened: false,
      isLoading: false,
      pagination: resetPagination(),
      options: {}, // paginationWatch mixin is dependent on this, but it is not used  for anything. It just has to exist.
      selectedSearchAttributes: [],
      taxJurisdictionToView: {},
    }
  },
  computed: {
    headers() {
      return [
        { text: this.$t('generalAttributes.name'), value: 'name' },
        {
          text: this.$t('entities.taxJurisdictions.attributes.fiscalize'),
          value: 'fiscalize',
        },
        {
          text: this.$t('miscellaneous.actions'),
          value: 'actions',
          sortable: false,
        },
      ]
    },
  },
  methods: {
    closeDetailsDialog() {
      this.isDetailsDialogOpened = false
      this.taxJurisdictionToView = {}
    },
    async fetchEntities(append = this.setParams()) {
      try {
        this.isLoading = true
        const response = await TaxJurisdiction.api.list(append)
        const data = response.data
        this.taxJurisdictions = data.data.map((it) => new TaxJurisdiction(it))
        this.updatePagination(data.metadata || data.meta, data.data.length)
      } catch (e) {
        console.error(e)
      } finally {
        this.isLoading = false
      }
    },
    openDetailsDialog(taxJurisdiction) {
      this.taxJurisdictionToView = { ...taxJurisdiction }
      this.isDetailsDialogOpened = true
    },
    updatePagination(metadata = null, dataLength = 0) {
      const page = metadata?.page ?? this.pagination.current_page ?? 1
      const size = metadata?.size ?? this.pagination.per_page ?? 10
      const total = metadata?.count ?? metadata?.total ?? dataLength
      const lastPage = size ? Math.ceil((total || 0) / size) : 0
      const from = total ? (page - 1) * size + 1 : 0
      const to = total ? Math.min(from + size - 1, total) : 0
      this.pagination.current_page = page
      this.pagination.per_page = size
      this.pagination.total = total
      this.pagination.last_page = lastPage
      this.pagination.from = from
      this.pagination.to = to
    },
    refresh() {
      this.pagination = resetPagination()
      this.fetchEntities()
    },
  },
  mounted() {
    return this.fetchEntities()
  },
}
</script>
